import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { PasswordInput } from '@/components/PasswordInput';
import { InputOTP, InputOTPGroup, InputOTPSlot } from '@/components/ui/input-otp';
import { CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';

/**
 * Unified 2FA login component shared between customer and admin portals.
 *
 * Props:
 *   - onSuccess(user)     : called after OTP verification succeeds. Receives the
 *                           logged-in user object (with __signup_status).
 *   - onSignupReview(info): called if the backend blocks login due to signup
 *                           pending_approval / rejected (customer flow only).
 *   - variant             : 'customer' | 'staff' — controls copy tone.
 */
export function UnifiedLogin({ onSuccess, onSignupReview, variant = 'customer' }) {
    const { loginStep1, loginStep2, loginResendOtp, loading } = useAuth();

    const [step, setStep]         = useState('credentials');
    const [identifier, setId]     = useState('');
    const [password, setPassword] = useState('');
    const [otp, setOtp]           = useState('');
    const [masked, setMasked]     = useState({ mobile: '', email: '' });
    const [channels, setChannels] = useState([]);
    const [sending, setSending]   = useState(false);
    const [resendCd, setResendCd] = useState(0);
    const [rememberDevice, setRememberDevice] = useState(false);
    const [tcAccepted, setTcAccepted]         = useState(false);

    const isMobile = /^[+\d\s\-()]{6,}$/.test(identifier) && !/@/.test(identifier);
    const digits   = identifier.replace(/\D/g, '');

    useEffect(() => {
        if (resendCd <= 0) return;
        const t = setInterval(() => setResendCd(v => Math.max(0, v - 1)), 1000);
        return () => clearInterval(t);
    }, [resendCd]);

    const submitCreds = async (e) => {
        e.preventDefault();
        if (!identifier.trim() || !password) { toast.error('Enter email/mobile and password'); return; }
        setSending(true);
        try {
            const r = await loginStep1(identifier.trim(), password);
            // Admin bypass: backend already issued token — go straight to success
            if (r && r.step === 'authenticated' && r.token) {
                toast.success('Login successful');
                onSuccess?.({ ...(r.user || {}), __signup_status: r.signup_status || 'active' });
                return;
            }
            setMasked({ mobile: r.masked_mobile || '', email: r.masked_email || '' });
            setChannels(r.channels || []);
            setStep('otp');
            setResendCd(30);
            const via = (r.channels || []).includes('email') ? 'email + mobile' : 'mobile';
            toast.success(`OTP भेज दी गयी — check your ${via}`);
        } catch (err) {
            const detail = err?.response?.data?.detail;
            if (detail && typeof detail === 'object' && (detail.code === 'signup_pending_approval' || detail.code === 'signup_rejected')) {
                onSignupReview?.(detail);
                return;
            }
            toast.error(typeof detail === 'string' ? detail : 'Login failed');
        } finally { setSending(false); }
    };

    const verify = async () => {
        if (otp.length !== 6) { toast.error('Enter 6-digit OTP'); return; }
        if (rememberDevice && !tcAccepted) {
            toast.error('Please accept Terms & Conditions to trust this device');
            return;
        }
        try {
            const u = await loginStep2(identifier.trim(), otp, {
                remember_device: rememberDevice,
                tc_accepted:     tcAccepted,
            });
            onSuccess?.(u);
        } catch (err) {
            const detail = err?.response?.data?.detail;
            toast.error(typeof detail === 'string' ? detail : 'Invalid OTP');
        }
    };

    const resend = async () => {
        if (resendCd > 0) return;
        try {
            await loginResendOtp(identifier.trim());
            toast.success('New OTP sent');
            setResendCd(30);
            setOtp('');
        } catch (err) {
            const detail = err?.response?.data?.detail;
            toast.error(typeof detail === 'string' ? detail : 'Resend failed');
        }
    };

    if (step === 'otp') {
        return (
            <div className='space-y-4' data-testid='auth-unified-otp-step'>
                <div className='rounded-lg bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200 dark:border-emerald-900 p-3 flex items-start gap-2 text-xs'>
                    <CheckCircle2 className='w-4 h-4 text-emerald-600 shrink-0 mt-0.5' />
                    <div>
                        <div className='font-semibold text-emerald-800 dark:text-emerald-200'>OTP भेज दी गयी</div>
                        {masked.mobile && <div>📱 WhatsApp/SMS: <b>+91 {masked.mobile}</b></div>}
                        {masked.email  && <div>✉️ Email: <b>{masked.email}</b></div>}
                        <div className='opacity-70 mt-1'>Valid for 10 minutes</div>
                    </div>
                </div>

                <div className='space-y-2'>
                    <Label>Enter 6-digit OTP / OTP दर्ज करें</Label>
                    <InputOTP maxLength={6} value={otp} onChange={setOtp} data-testid='auth-unified-otp-input'>
                        <InputOTPGroup>
                            {[0,1,2,3,4,5].map(i => <InputOTPSlot index={i} key={i} />)}
                        </InputOTPGroup>
                    </InputOTP>
                </div>

                {/* Trusted-device opt-in — bundled T&C acceptance */}
                <div className='rounded-md border p-3 bg-muted/30' data-testid='trusted-device-block'>
                    <label className='flex items-start gap-2 cursor-pointer text-xs' data-testid='trusted-device-checkbox'>
                        <input
                            type='checkbox'
                            className='mt-0.5 shrink-0'
                            checked={rememberDevice && tcAccepted}
                            onChange={e => { setRememberDevice(e.target.checked); setTcAccepted(e.target.checked); }}
                        />
                        <span>
                            <b>Remember this device for today</b> & I accept the{' '}
                            <a href='/terms' target='_blank' rel='noreferrer' className='underline text-primary'>Terms &amp; Conditions</a>
                        </span>
                    </label>
                </div>

                <Button
                    onClick={verify}
                    disabled={loading || otp.length !== 6}
                    className='w-full min-h-[48px]'
                    data-testid='auth-unified-verify'
                >
                    {loading ? 'Verifying…' : 'Verify & Login'}
                </Button>

                <div className='flex items-center justify-between text-xs'>
                    <button
                        type='button'
                        onClick={() => { setStep('credentials'); setOtp(''); }}
                        className='text-muted-foreground underline'
                        data-testid='auth-unified-back'
                    >
                        ← Change email/mobile
                    </button>
                    <button
                        type='button'
                        onClick={resend}
                        disabled={resendCd > 0}
                        className={`underline ${resendCd > 0 ? 'text-muted-foreground opacity-50 cursor-not-allowed' : 'text-primary'}`}
                        data-testid='auth-unified-resend'
                    >
                        {resendCd > 0 ? `Resend in ${resendCd}s` : 'Resend OTP'}
                    </button>
                </div>
            </div>
        );
    }

    return (
        <form onSubmit={submitCreds} className='space-y-4' data-testid='auth-unified-credentials-step'>
            <div className='space-y-1'>
                <Label>Email or Mobile / ईमेल या मोबाइल <span className='text-destructive'>*</span></Label>
                <Input
                    required
                    autoComplete='username'
                    value={identifier}
                    onChange={e => setId(e.target.value)}
                    placeholder={variant === 'staff' ? 'staff@company.com  or  9876543210' : 'you@example.com  or  9876543210'}
                    className='min-h-[48px]'
                    data-testid={variant === 'staff' ? 'admin-email-input' : 'auth-email-input'}
                />
                {identifier && (
                    <p className='text-[11px] text-muted-foreground'>
                        {isMobile
                            ? <span>📱 Detected as mobile — <b>{digits.slice(-10) || digits}</b></span>
                            : <span>✉️ Detected as email</span>}
                    </p>
                )}
            </div>
            <div className='space-y-1'>
                <Label>Password / पासवर्ड <span className='text-destructive'>*</span></Label>
                <PasswordInput
                    required
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    placeholder='Enter your password'
                    className='min-h-[48px]'
                    data-testid={variant === 'staff' ? 'admin-password-input' : 'auth-password-input'}
                />
            </div>
            <Button
                type='submit'
                disabled={sending || loading}
                className='w-full min-h-[48px]'
                data-testid={variant === 'staff' ? 'admin-login-submit' : 'auth-email-submit'}
            >
                {sending ? 'Logging in…' : 'Login'}
            </Button>
            <p className='text-[11px] text-center text-muted-foreground'>
                🔒 Two-step सुरक्षा: password check होते ही आपके registered email + mobile पर OTP आएगा।
            </p>
        </form>
    );
}
